import { PriorityHigh, PriorityLow, PriorityMedium, X } from '@griply/assets';
import { Task, TaskPriorityOptions, UpdateTask } from '@griply/types';
import { getSharedPriorityValue, useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { QuickActionRow } from 'shared/components/ui/quick-action-row';
import { Tooltip } from 'shared/components/ui/tooltip';

export type TaskPriorityQuickActionsProps = {
  tasks: Task[];
  onClose: () => void;
  onAction?: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
};

export const TaskPriorityQuickActions: React.FC<
  TaskPriorityQuickActionsProps
> = ({ tasks, onClose, onAction, onUpdate }) => {
  const { t } = useTranslation();
  const { submit: updateTasks } = useUpdateTasks();

  const priority = useMemo(() => getSharedPriorityValue(tasks), [tasks]);

  const highPriorityRef = useRef<HTMLButtonElement>(null);
  const mediumPriorityRef = useRef<HTMLButtonElement>(null);
  const lowPriorityRef = useRef<HTMLButtonElement>(null);
  const clearPriorityRef = useRef<HTMLButtonElement>(null);

  const applyPriority = (value: TaskPriorityOptions | null) => {
    onUpdate
      ? tasks.forEach((task) =>
          onUpdate(task, { id: task.id, priority: value }),
        )
      : updateTasks(tasks.map((task) => ({ ...task, priority: value })));

    onAction?.();
    onClose();
  };

  const setHighPriority = () => applyPriority(TaskPriorityOptions.High);
  const setMediumPriority = () => applyPriority(TaskPriorityOptions.Medium);
  const setLowPriority = () => applyPriority(TaskPriorityOptions.Low);
  const clearPriority = () => applyPriority(null);

  return (
    <QuickActionRow
      label={t('context-menus.task.multi-select.options.priority.label')}
      shortcut="P"
    >
      <IconButton
        ref={highPriorityRef}
        icon={PriorityHigh}
        onClick={setHighPriority}
        color={Colors.Error}
        highlighted={priority === TaskPriorityOptions.High}
        aria-label={t('task.priority.options.high.label')}
      />
      <Tooltip
        referenceElement={highPriorityRef}
        label={t('task.priority.options.high.label')}
      />
      <IconButton
        ref={mediumPriorityRef}
        icon={PriorityMedium}
        onClick={setMediumPriority}
        color={Colors.Warning}
        highlighted={priority === TaskPriorityOptions.Medium}
        aria-label={t('task.priority.options.medium.label')}
      />
      <Tooltip
        referenceElement={mediumPriorityRef}
        label={t('task.priority.options.medium.label')}
      />
      <IconButton
        ref={lowPriorityRef}
        icon={PriorityLow}
        onClick={setLowPriority}
        color={Colors.Info}
        highlighted={priority === TaskPriorityOptions.Low}
        aria-label={t('task.priority.options.low.label')}
      />
      <Tooltip
        referenceElement={lowPriorityRef}
        label={t('task.priority.options.low.label')}
      />
      {priority !== TaskPriorityOptions.None && (
        <>
          <IconButton
            ref={clearPriorityRef}
            icon={X}
            onClick={clearPriority}
            color={Colors.Default}
            aria-label={t('task.priority.clear.label')}
          />
          <Tooltip
            referenceElement={clearPriorityRef}
            label={t('task.priority.clear.label')}
          />
        </>
      )}
    </QuickActionRow>
  );
};
